"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { Brand } from "@webuild-ai/react-auth";

import { PageTitle } from "@/components/UI/PageTitle";

interface DashboardLayoutProps {
  /** Dashboard title displayed in header (i18n key or raw string) */
  title?: string;
  /** Dashboard subtitle/description (i18n key or raw string) */
  subtitle?: string;
  /** Current brand configuration */
  brand: Brand;
  /** Dashboard content */
  children: React.ReactNode;
}

/**
 * Shared layout wrapper for all dashboard types.
 * Provides consistent header with PageTitle and full-height content area.
 * Supports localised titles/subtitles via i18n translation keys.
 */
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title = "Dashboard",
  subtitle = "Dashboard Description",
  brand,
  children,
}) => {
  const { t } = useTranslation();

  // Use translation if available, otherwise fall back to raw string
  const localisedTitle = t(title, { defaultValue: title });
  const localisedSubtitle = t(subtitle, { defaultValue: subtitle });

  return (
    <div className="h-full flex flex-col p-4">
      <PageTitle
        centered
        dataId="dashboard-title"
        icon="solar:chart-2-outline"
        img={`/brands/${brand.prefix || "webuild"}/dashboard.svg`}
        subtitle={localisedSubtitle}
        title={localisedTitle}
      />
      <div className="flex-1 relative min-h-0 mt-4">{children}</div>
    </div>
  );
};
